import { produce } from 'immer'
import type { StateCreator } from 'zustand'

import { middleware } from '../middleware'
import type { Address, Store } from '../types'
import type { CreditCheckStatus } from './checkoutFlowSlice'

type OrderRequestStatus = 'success' | 'initial'
type OrderResponseState = {
  /**
   * CDP SOS order id after successful purchase
   */
  id: number | null
  /**
   * CDP SOS order request status. Used to determinate if order has been sent, to show the thank you page or show the error page.
   */
  status: OrderRequestStatus
  isExistingCustomer?: boolean
  creditCheckStatus?: CreditCheckStatus
  requiresAuth?: boolean
}

type OrderConfirmationState = {
  orderResponse: OrderResponseState
  orderDetailsPreview: {
    deliveryAddress?: Address
    startDate?: string
  }
}

type OrderConfirmationActions = {
  setOrderResponse: (orderResponse: OrderResponseState) => void
  resetOrderConfirmation: () => void
  setOrderDetailsPreview: (deliveryAddress: Address, startDate: string | undefined) => void
}

/**
 * Information about order confirmation
 */
export type OrderConfirmationStore = {
  orderConfirmation: OrderConfirmationState & OrderConfirmationActions
}

// Good practice to keep it still, even while empty
export const initialState: OrderConfirmationState = {
  orderResponse: {
    id: null,
    status: 'initial',
    creditCheckStatus: undefined,
    isExistingCustomer: false,
    requiresAuth: false,
  },
  orderDetailsPreview: { deliveryAddress: undefined, startDate: undefined },
}

export const creator: StateCreator<
  Store,
  [['zustand/persist', unknown]],
  [],
  OrderConfirmationStore
> = (set, get) => ({
  orderConfirmation: {
    ...initialState,
    setOrderResponse: (orderResponse) =>
      set(
        produce(get(), (state: Store) => {
          state.orderConfirmation.orderResponse = orderResponse
        }),
      ),
    resetOrderConfirmation: () =>
      set(
        produce(get(), (state: Store) => {
          state.orderConfirmation.orderResponse = initialState.orderResponse
          state.orderConfirmation.orderDetailsPreview = initialState.orderDetailsPreview
        }),
      ),
    setOrderDetailsPreview: (address, startDate) =>
      set(
        produce(get(), (state: Store) => {
          state.orderConfirmation.orderDetailsPreview.deliveryAddress = address
          state.orderConfirmation.orderDetailsPreview.startDate = startDate
        }),
      ),
  },
})

export const orderConfirmationSlice = middleware(creator, (prevState, _set, get, _store) => {
  const {
    orderConfirmation: {
      orderResponse: { id: prevOrderId },
    },
    checkoutData: {
      meter: { address },
      delivery: { startDate },
    },
  } = prevState

  const {
    orderResponse: { id: orderId },
    setOrderDetailsPreview,
  } = get().orderConfirmation

  if (orderId && prevOrderId !== orderId) {
    setOrderDetailsPreview(address, startDate)
  }
})
